import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, AlertTriangle, CheckCircle2, Wrench } from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import { Badge, StatusBadge } from "../components/Badges";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const MISMATCH_LABEL = {
  orphaned: ["Orphaned (no job)", "b-slate"],
  stale: ["Stale (job moved on)", "b-amber"],
};

export default function Reconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [fixing, setFixing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/equipment/lab-status-reconciliation")
      .then((r) => { setData(r.data); setSelected(new Set()); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const mismatches = data?.mismatches || [];
  const allSelected = mismatches.length > 0 && selected.size === mismatches.length;

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(mismatches.map((m) => m.equipment_id)));

  const fixSelected = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const confirmed = window.confirm(
      `Fix ${ids.length} selected equipment record${ids.length === 1 ? "" : "s"}?\n\n` +
      `Orphaned records fall back to their due-date-derived status (e.g. Active at client). ` +
      `Stale records are re-synced to their linked job's real current status. Cannot be undone.`
    );
    if (!confirmed) return;
    setFixing(true);
    try {
      const r = await api.post("/equipment/lab-status-reconciliation/fix", { equipment_ids: ids });
      toast.success(`Fixed ${r.data.fixed} of ${r.data.requested} selected record(s)`);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setFixing(false); }
  };

  if (loading && !data) return <Loader />;

  return (
    <>
      <PageHead crumb="Records" title="Equipment Lab-Status Reconciliation"
        sub="Finds equipment showing 'In Lab' on the Register that no longer matches the real Active Lab truth, and lets you correct them."
        actions={
          <button className="btn ghost" onClick={load} disabled={loading} data-testid="reconciliation-refresh-btn">
            <RefreshCw size={15} /> {loading ? "Scanning…" : "Refresh scan"}
          </button>
        } />

      <div className="form-grid" style={{ marginBottom: 16 }}>
        <div className="card pad" data-testid="reconciliation-total-in-lab">
          <div className="t-mut" style={{ fontSize: 12 }}>Shown as "In Lab" on Register</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{data?.total_shown_in_lab ?? "—"}</div>
        </div>
        <div className="card pad" data-testid="reconciliation-genuine-count" style={{ borderLeft: "3px solid var(--green, #1f8a4d)" }}>
          <div className="t-mut" style={{ fontSize: 12 }}>Genuinely in the lab (matches Active Lab)</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "var(--green, #1f8a4d)" }}>{data?.genuinely_in_lab ?? "—"}</div>
        </div>
        <div className="card pad" data-testid="reconciliation-mismatch-count" style={{ borderLeft: "3px solid var(--red, #b3261e)" }}>
          <div className="t-mut" style={{ fontSize: 12 }}>Mismatches found</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "var(--red, #b3261e)" }}>{data?.mismatch_count ?? "—"}</div>
          {!!data?.mismatch_count && (
            <div className="t-mut" style={{ fontSize: 11.5, marginTop: 2 }}>
              {data.orphaned_count} orphaned · {data.stale_count} stale
            </div>
          )}
        </div>
      </div>

      {mismatches.length === 0 ? (
        <div className="card pad" data-testid="reconciliation-empty" style={{ textAlign: "center", padding: 32 }}>
          <CheckCircle2 size={28} color="var(--green, #1f8a4d)" style={{ marginBottom: 8 }} />
          <div className="t-strong">No mismatches found — the Equipment Register's "In Lab" items all match the real Active Lab.</div>
        </div>
      ) : (
        <div className="card pad">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
            <div className="t-mut" style={{ fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={14} color="var(--gold-deep)" /> {selected.size} of {mismatches.length} selected
            </div>
            <button className="btn gold" onClick={fixSelected} disabled={selected.size === 0 || fixing} data-testid="reconciliation-fix-selected-btn">
              <Wrench size={14} /> {fixing ? "Fixing…" : `Fix ${selected.size} selected`}
            </button>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="reconciliation-select-all" />
                  </th>
                  <th>Customer</th>
                  <th>Delivery note</th>
                  <th>Equipment</th>
                  <th>Serial</th>
                  <th>Currently shows</th>
                  <th>Issue</th>
                  <th>Real status</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map((m) => {
                  const [label, cls] = MISMATCH_LABEL[m.mismatch_type] || [m.mismatch_type, "b-slate"];
                  return (
                    <tr key={m.equipment_id} data-testid={`reconciliation-row-${m.equipment_id}`}>
                      <td>
                        <input type="checkbox" checked={selected.has(m.equipment_id)}
                          onChange={() => toggleOne(m.equipment_id)}
                          data-testid={`reconciliation-checkbox-${m.equipment_id}`} />
                      </td>
                      <td className="t-strong">{m.customer_name || "—"}</td>
                      <td className="mono">{m.delivery_note_number || "—"}</td>
                      <td>{m.description || "—"}</td>
                      <td className="mono t-mut">{m.serial_number || "—"}</td>
                      <td><StatusBadge status={m.current_status} /></td>
                      <td><Badge label={label} cls={cls} /></td>
                      <td>{m.real_status
                        ? <StatusBadge status={m.real_status} />
                        : <span className="t-mut" style={{ fontSize: 12 }}>No linked job — will fall back to Active/Due soon/Overdue</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
